import React from "react";
import styled from "styled-components";
import { Application, makeRequest, Process } from "../api";
import { BatchesContext } from "../BatchesContext";
import { UserContext } from "../UserContext";
import { getCapturedQueryParams, trackAnalyticsEvents } from "./Analytics";
import { DynamicForm } from "./DynamicForm";
import { LoadingIndicator } from "./Utils";

const FormContainer = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    padding: 20px;
  }

  h4 {
    font-weight: 600;
    margin: 0 0 30px 0;
    font-size: 24px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 20px;
      margin: 0 0 20px 0;
    }
  }
`;

interface Props {
  /** Callback executed after an application is submitted */
  onSubmitted: (application: Application, process: Process) => void;
  /** Set to true to prevent automatic userId/email binding, for admin use cases. */
  isAnonymous?: boolean;
  /** Required batchId for the form */
  batchId: string;
  /** Callback when user has already applied with the email */
  onDuplicateApplication?: (email: string) => void;
}

export const ApplyForm = (props: Props) => {
  const batches = React.useContext(BatchesContext)?.batches;
  const userContext = React.useContext(UserContext);
  const [process, setProcess] = React.useState<Process | null>();
  const [email, setEmail] = React.useState(
    props.isAnonymous ? "" : userContext?.user?.email || ""
  );

  React.useEffect(() => {
    if (!props.isAnonymous && userContext?.user?.email) {
      setEmail(userContext?.user?.email);
    }
  }, [userContext, props.isAnonymous]);

  React.useEffect(() => {
    const batch = batches?.find((b) => b._id === props.batchId);
    if (batch) {
      makeRequest(`/api/processConfigBlobs/${batch.processId}`, "GET").then(
        (blob) => {
          setProcess(JSON.parse(blob.json) as any);
        }
      );
    }
  }, [props.batchId, batches]);

  const currentBatch = batches?.find((b) => b._id === props.batchId);
  
  return (
    <FormContainer>
      <h4>{currentBatch?.name} Enrollment Form</h4>
      <p>Upskill, open new opportunities and transform your career.</p>
      {process ? (
        <DynamicForm
          configuration={process.applicationForm}
          preprocess={(app: Application) => {
            const params = getCapturedQueryParams();
            if (params.size > 0) {
              app.tracking = app.tracking || ({} as any);
              if (params.get("utm_source")) app.tracking.source = params.get("utm_source");
              if (params.get("utm_medium")) app.tracking.medium = params.get("utm_medium");
              if (params.get("utm_campaign")) app.tracking.campaign = params.get("utm_campaign");
              if (params.get("utm_term")) app.tracking.term = params.get("utm_term");
              if (params.get("utm_content")) app.tracking.content = params.get("utm_content");
            } else if (app.heardFrom) {
              app.tracking = { source: app.heardFrom, medium: '', campaign: '', term: '', content: '' };
            }
            return app;
          }}
          onSubmitted={(application: Application) => {
            trackAnalyticsEvents("SubmitApplication");
            props.onSubmitted(application, process);
          }}
          initialEntry={{ batchId: props.batchId, email }}
          onDuplicateApplication={props.onDuplicateApplication}
        />
      ) : (
        <LoadingIndicator />
      )}
    </FormContainer>
  );
};
